// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { useIdleTimer } from 'react-idle-timer'
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import { minutesToMilliseconds } from "utils/MathUtils";
import { Config } from "config";
import { useDgAuth } from 'hooks/DgAuthentication';
import { setLastActiveUpdateTime, getLastActiveUpdateTime, setLastActiveTime, getLastActiveTime } from 'utils/localStorageHelpers';

export function useSessionTimeout() {
    const { isAuthenticated, logout, hasOrgClaims } = useDgAuth();
    const idleTimeoutInMinsRef = useRef<number>(minutesToMilliseconds(Config.idleTimeoutInMinutes));
    const idlePromptTimeoutInMinsRef = useRef<number>(minutesToMilliseconds(Config.idlePromptTimeoutInMinutes));

    const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);

    const onActionDebounceMs = 1000 * 30;
    const minUpdateIntervalMs = 1000 * 60 * 30;

    const { reset } = useIdleTimer({
        startOnMount: isAuthenticated,
        timeout: idleTimeoutInMinsRef.current,
        promptTimeout: idlePromptTimeoutInMinsRef.current,
        crossTab: true,
        onPrompt: () => setTimeoutModalOpen(true),
        onIdle: onEndSession,
        onAction: onAction,
        debounce: onActionDebounceMs
    })

    useEffect(() => {
        if (isAuthenticated) {
            setLastActiveTime(new Date());
        }
    }, [isAuthenticated])

    useEffect(() => {
        function onWindowVisibilityChanged() {
            if (!document.hidden) {
                const lastActiveTime = getLastActiveTime();
                if (lastActiveTime === null) {
                    return;
                }

                const buffer = 1000 * 60;
                const now = new Date();
                const timeSinceLastActive = now.getTime() - lastActiveTime.getTime();
                if (timeSinceLastActive > (idleTimeoutInMinsRef.current + idlePromptTimeoutInMinsRef.current + buffer)) {
                    logout({ returnTo: `${Config.baseUrl}/session-expired` })
                }
            }
        }

        document.addEventListener('visibilitychange', onWindowVisibilityChanged);
        return () => {
            document.removeEventListener('visibilitychange', onWindowVisibilityChanged);
        };
    }, [logout])

    async function onAction() {
        const lastActiveUpdateTime = getLastActiveUpdateTime();
        const now = new Date();
        setLastActiveTime(now);
        if (!hasOrgClaims || (!!lastActiveUpdateTime && now.getTime() - lastActiveUpdateTime.getTime() < minUpdateIntervalMs))
        {
            return;
        }
        setLastActiveUpdateTime(now);
        const url = `${Config.organizationApiUrl}/user/active`;
        await axios.post(url);
    }

    function onEndSession() {
        logout({ returnTo: `${Config.baseUrl}/session-expired` })
    }

    function onContinueSession() {
        reset()
        setTimeoutModalOpen(false)
    }

    return isAuthenticated ? (
        <SessionTimeoutModal
            isOpen={timeoutModalOpen}
            onEndSession={onEndSession}
            onContinueSession={onContinueSession} />
    ) : <></>;
}

interface TimeoutModalProps {
    isOpen: boolean;
    onEndSession: () => void;
    onContinueSession: () => void;
}

export const SessionTimeoutModal = (props: TimeoutModalProps): JSX.Element => {
    const dialogContentProps = {
        type: DialogType.normal,
        title: 'Session timeout',
        subText: `Your session is about to expire. Do you want to stay signed in?`,
        showCloseButton: false,
        styles: { subText: { whiteSpace: 'pre-line' } }
    };

    return (
        <Dialog
            hidden={!props.isOpen}
            dialogContentProps={dialogContentProps}
            modalProps={{
                isBlocking: true,
                topOffsetFixed: true,
                styles: {
                    main: {
                        selectors: {
                            '@media (min-width: 480px)': {
                                maxWidth: '380px'
                            }
                        }
                    }
                }
            }}>
            <DialogFooter>
                <DefaultButton onClick={props.onEndSession} text="No, sign me out" />
                <PrimaryButton onClick={props.onContinueSession} text="Yes, remain signed in" />
            </DialogFooter>
        </Dialog>
    )
}
